import { useEffect, useCallback, useRef, useState } from "react";
import { useLatest } from "hooks/useLatest";
import { STATUSES } from "./constants";
import {
  UseRequestService,
  UseRequestOptions,
  UseRequestResult,
  InternalState,
  MutateDataFunction,
} from "./types";

export const useRequest = <TData, TParams extends any[]>(
  service: UseRequestService<TData, TParams>,
  options: UseRequestOptions<TParams>,
): UseRequestResult<TData, TParams> => {
  const mountedRef = useRef(true);
  const serviceRef = useLatest(service);

  const isManual = options.manual === true;

  const [state, setState] = useState<InternalState<TData>>(() => {
    if (isManual) {
      return {
        status: STATUSES.IDLE,
        data: undefined,
        error: undefined,
      };
    }
    return {
      status: STATUSES.LOADING,
      data: undefined,
      error: undefined,
    };
  });

  const run = useCallback(
    async (...args: TParams) => {
      setState((prev) => ({
        status: STATUSES.LOADING,
        data: prev.status === STATUSES.SUCCESS ? prev.data : undefined,
        error: undefined,
      }));

      try {
        const result = await serviceRef.current(...args);

        if (mountedRef.current) {
          setState({
            status: STATUSES.SUCCESS,
            data: result,
            error: undefined,
          });
        }

        return result;
      } catch (err) {
        if (mountedRef.current) {
          setState({
            status: STATUSES.ERROR,
            data: undefined,
            error: err,
          });
        }
        throw err;
      }
    },
    [serviceRef],
  );

  const mutateData: MutateDataFunction<TData> = useCallback(({ updater }) => {
    setState((prev) => {
      if (prev.status !== STATUSES.SUCCESS || prev.data === undefined) {
        return prev;
      }

      return {
        ...prev,
        data: updater(prev.data),
      };
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isManual) {
      run(...options.defaultParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    isLoading: state.status === STATUSES.LOADING,
    run,
    mutateData,
  };
};
